import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const Row = ({ label, value }) => (
  <div className="admin-wiring-row">
    <span className="lbl">{label}</span>
    <code>{value || '—'}</code>
  </div>
);

const AdminWiring = ({ role, onChanged }) => {
  const { plans, coreStorage, booster } = useContracts();
  const notify = useNotify();
  const [busy, setBusy] = useState('');
  const [info, setInfo] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read || !plans?.read || !booster?.read) return;
      try {
        const [csPlans, csBoost, plBoost, bsPlans] = await Promise.all([
          coreStorage.read.plansContract().catch(() => ''),
          coreStorage.read.boosterContract().catch(() => ''),
          plans.read.booster().catch(() => ''),
          booster.read.plansContract().catch(() => '')
        ]);
        if (!cancelled) setInfo({ csPlans, csBoost, plBoost, bsPlans });
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [plans, coreStorage, booster]);

  const send = async (key, contract, method, addr) => {
    if (!ethers.utils.isAddress(addr)) return notify.error('Invalid', 'Bad address');
    setBusy(key);
    try {
      const tx = await contract.write[method](addr);
      notify.info('Submitting', `${method} sent…`);
      await tx.wait();
      notify.success('Updated', method);
      onChanged && onChanged();
    } catch (e) { notify.error('Failed', parseError(e)); }
    finally { setBusy(''); }
  };

  const Form = ({ id, contract, method, label, currentLabel, currentValue }) => {
    const [val, setVal] = useState('');
    return (
      <div className="admin-tile">
        <div className="admin-tile-title">{label}</div>
        <Row label={currentLabel} value={currentValue} />
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="0x…" style={{ marginTop: 8 }} />
        <button
          className="btn btn-primary btn-block"
          style={{ marginTop: 8 }}
          disabled={busy === id || !contract?.write}
          onClick={() => send(id, contract, method, val)}
        >
          {busy === id ? <ButtonLoader label="Updating" /> : 'Update'}
        </button>
      </div>
    );
  };

  return (
    <AdminSection
      title="Contract Wiring"
      subtitle="Connect Plans / Booster references inside CoreStorage and cross-link Plans ↔ Booster."
      ownerOnly
      role={role}
    >
      <div className="admin-grid-2">
        <Form id="csPlans"  contract={coreStorage} method="setPlansContract"   label="CoreStorage → Plans"   currentLabel="Linked Plans"   currentValue={info.csPlans} />
        <Form id="csBoost"  contract={coreStorage} method="setBoosterContract" label="CoreStorage → Booster" currentLabel="Linked Booster" currentValue={info.csBoost} />
        <Form id="plBoost"  contract={plans}       method="setBoosterContract" label="Plans → Booster"       currentLabel="Linked Booster" currentValue={info.plBoost} />
        <Form id="bsPlans"  contract={booster}     method="setPlansContract"   label="Booster → Plans"       currentLabel="Linked Plans"   currentValue={info.bsPlans} />
      </div>
    </AdminSection>
  );
};

export default AdminWiring;
